import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Icon, type IconName } from '../icons/Icon';

interface MenuProps {
  /** The button that opened the menu: the menu sits under it and focus goes back to it. */
  anchor: HTMLElement | null;
  label: string;
  onClose: () => void;
  children: ReactNode;
}

interface MenuItemProps {
  icon?: IconName;
  /** Destructive: red text, never a red fill (rule 6), and always with its word (rule 12). */
  danger?: boolean;
  /** For a choice among several (`menuitemradio`): the current one. */
  checked?: boolean;
  disabled?: boolean;
  onSelect: () => void;
  children: ReactNode;
}

/** An item closes its menu before acting, so the action may open another one. */
const CloseContext = createContext<() => void>(() => undefined);

const WIDTH = 240;
const GUTTER = 16;
const GAP = 4;

function place(menu: HTMLElement, anchor: HTMLElement): { left: number; top: number } {
  const r = anchor.getBoundingClientRect();
  const h = menu.offsetHeight;
  const left = Math.max(GUTTER, Math.min(r.right - WIDTH, window.innerWidth - WIDTH - GUTTER));
  const below = r.bottom + h + GAP * 2 <= window.innerHeight;
  return { left, top: below ? r.bottom + GAP : Math.max(GAP * 2, r.top - h - GAP) };
}

function moveFocus(menu: HTMLElement, step: number): void {
  const items = [...menu.querySelectorAll<HTMLButtonElement>('button:not([disabled])')];
  const at = items.indexOf(document.activeElement as HTMLButtonElement);
  items[(at + step + items.length) % items.length]?.focus();
}

/** A menu of actions under a button (row menus, «Cambia stato»): arrows move, Escape and a click
 *  outside close it, focus returns to the button. One component for every menu (CLAUDE.md §8). */
export function Menu({ anchor, label, onClose, children }: MenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    if (ref.current !== null && anchor !== null) setAt(place(ref.current, anchor));
    ref.current?.querySelector<HTMLButtonElement>('button:not([disabled])')?.focus();
  }, [anchor]);
  useEffect(() => {
    const outside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!ref.current?.contains(target) && !anchor?.contains(target)) onClose();
    };
    document.addEventListener('mousedown', outside);
    return () => document.removeEventListener('mousedown', outside);
  }, [anchor, onClose]);
  const close = () => {
    onClose();
    anchor?.focus();
  };
  return (
    <div
      ref={ref}
      className="menu"
      role="menu"
      aria-label={label}
      style={{ width: WIDTH, left: at?.left ?? 0, top: at?.top ?? 0, opacity: at === null ? 0 : 1 }}
      onKeyDown={(e) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          if (ref.current !== null) moveFocus(ref.current, e.key === 'ArrowDown' ? 1 : -1);
        }
        if (e.key === 'Escape') {
          e.stopPropagation();
          close();
        }
      }}
      // A choice in a card's menu is not a click on the card.
      onClick={(e) => e.stopPropagation()}
    >
      <CloseContext.Provider value={onClose}>{children}</CloseContext.Provider>
    </div>
  );
}

export function MenuItem({ icon, danger, checked, disabled, onSelect, children }: MenuItemProps) {
  const close = useContext(CloseContext);
  return (
    <button
      type="button"
      role={checked === undefined ? 'menuitem' : 'menuitemradio'}
      aria-checked={checked}
      className={danger === true ? 'dz' : undefined}
      disabled={disabled}
      onClick={() => {
        close();
        onSelect();
      }}
    >
      {icon === undefined ? null : <Icon name={icon} size={15} />}
      {children}
    </button>
  );
}

export function MenuSeparator() {
  return <hr />;
}
